import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Users, Inbox, LogOut, Trash2, Plus, Upload, ClipboardList } from "lucide-react";
import { api, hirePhoto } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Logo } from "../components/Header";

const TABS = [
  { key: "vacancies", icon: Briefcase },
  { key: "hires", icon: Users },
  { key: "inquiries", icon: Inbox },
  { key: "applications", icon: ClipboardList },
];

export default function AdminDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("vacancies");
  const [vacancies, setVacancies] = useState([]);
  const [hires, setHires] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [vac, setVac] = useState({ title: "", country: "", industry: "", salary: "", currency: "SAR", openings: 1 });
  const [hire, setHire] = useState({ name: "", profession: "", country: "", visa_status: "Visa Stamped", photo_url: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, h, i, a] = await Promise.all([
        api.get("/vacancies"), api.get("/hires"), api.get("/admin/inquiries"), api.get("/admin/applications"),
      ]);
      setVacancies(v.data);
      setHires(h.data);
      setInquiries(i.data);
      setApplications(a.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("hh_admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("hh_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const addVacancy = async (e) => {
    e.preventDefault();
    await api.post("/admin/vacancies", { ...vac, openings: Number(vac.openings) });
    toast.success(t("admin.saved"));
    setVac({ title: "", country: "", industry: "", salary: "", currency: "SAR", openings: 1 });
    load();
  };

  const addHire = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(hire).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("photo", file);
    await api.post("/admin/hires", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(t("admin.saved"));
    setHire({ name: "", profession: "", country: "", visa_status: "Visa Stamped", photo_url: "" });
    setFile(null);
    load();
  };

  const del = async (path) => {
    await api.delete(path);
    toast.success(t("admin.deleted"));
    load();
  };

  const setStatus = async (kind, id, status) => {
    await api.patch(`/admin/${kind}/${id}`, { status });
    load();
  };

  const logout = () => {
    localStorage.removeItem("hh_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0B132B]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#080E21] px-6 sm:px-10 py-4 flex items-center justify-between">
        <Logo />
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-red-400 transition-colors">
          <LogOut className="w-4 h-4" /> {t("admin.logout")}
        </button>
      </header>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map(({ key, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                tab === key ? "bg-[#E8842C] text-white" : "border border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" /> {t(`admin.${key}`)}
              {key === "inquiries" && inquiries.length > 0 && (
                <span className="bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full">{inquiries.length}</span>
              )}
              {key === "applications" && applications.length > 0 && (
                <span className="bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full">{applications.length}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "vacancies" && (
          <div data-testid="admin-vacancies-panel">
            <form onSubmit={addVacancy} className="rounded-2xl border border-white/10 bg-[#111D38] p-6 mb-8 grid sm:grid-cols-3 gap-4">
              <input data-testid="vac-title" required placeholder={t("admin.title_field")} className="field !bg-white/5 !text-white" value={vac.title} onChange={(e) => setVac({ ...vac, title: e.target.value })} />
              <input data-testid="vac-country" required placeholder={t("admin.country")} className="field !bg-white/5 !text-white" value={vac.country} onChange={(e) => setVac({ ...vac, country: e.target.value })} />
              <input data-testid="vac-industry" required placeholder={t("admin.industry")} className="field !bg-white/5 !text-white" value={vac.industry} onChange={(e) => setVac({ ...vac, industry: e.target.value })} />
              <input data-testid="vac-salary" required placeholder={t("admin.salary")} className="field !bg-white/5 !text-white" value={vac.salary} onChange={(e) => setVac({ ...vac, salary: e.target.value })} />
              <select data-testid="vac-currency" className="field !bg-white/5 !text-white" value={vac.currency} onChange={(e) => setVac({ ...vac, currency: e.target.value })}>
                {["SAR", "AED", "QAR", "KWD", "BHD", "OMR"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input data-testid="vac-openings" type="number" min="1" placeholder={t("admin.openings")} className="field !bg-white/5 !text-white" value={vac.openings} onChange={(e) => setVac({ ...vac, openings: e.target.value })} />
              <button data-testid="vac-add-btn" className="btn-primary justify-center sm:col-span-3 !py-3 text-sm"><Plus className="w-4 h-4" /> {t("admin.add_vacancy")}</button>
            </form>
            <div className="space-y-3">
              {vacancies.map((v, i) => (
                <div key={v.id} data-testid={`admin-vacancy-${i}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#111D38] px-5 py-4">
                  <div className="text-sm">
                    <span className="font-bold text-white">{v.title}</span>
                    <span className="text-slate-400 ms-3"> — {v.country} • {v.salary} {v.currency} • {v.openings}</span>
                  </div>
                  <button data-testid={`admin-vacancy-del-${i}`} onClick={() => del(`/admin/vacancies/${v.id}`)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "hires" && (
          <div data-testid="admin-hires-panel">
            <form onSubmit={addHire} className="rounded-2xl border border-white/10 bg-[#111D38] p-6 mb-8 grid sm:grid-cols-2 gap-4">
              <input data-testid="hire-name" required placeholder={t("admin.name")} className="field !bg-white/5 !text-white" value={hire.name} onChange={(e) => setHire({ ...hire, name: e.target.value })} />
              <input data-testid="hire-profession" required placeholder={t("admin.profession")} className="field !bg-white/5 !text-white" value={hire.profession} onChange={(e) => setHire({ ...hire, profession: e.target.value })} />
              <input data-testid="hire-country" required placeholder={t("admin.country")} className="field !bg-white/5 !text-white" value={hire.country} onChange={(e) => setHire({ ...hire, country: e.target.value })} />
              <select data-testid="hire-visa-status" className="field !bg-white/5 !text-white" value={hire.visa_status} onChange={(e) => setHire({ ...hire, visa_status: e.target.value })}>
                {["Visa Stamped", "Flight Dispatched", "Joined"].map((s) => <option key={s}>{s}</option>)}
              </select>
              <label className="field !bg-white/5 !text-white flex items-center gap-2 cursor-pointer" data-testid="hire-photo-upload">
                <Upload className="w-4 h-4 text-[#F4511E]" />
                <span className="text-sm truncate">{file ? file.name : t("admin.photo")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              </label>
              <input data-testid="hire-photo-url" placeholder={t("admin.photo_url")} className="field !bg-white/5 !text-white" value={hire.photo_url} onChange={(e) => setHire({ ...hire, photo_url: e.target.value })} />
              <button data-testid="hire-add-btn" className="btn-primary justify-center sm:col-span-2 !py-3 text-sm"><Plus className="w-4 h-4" /> {t("admin.add_hire")}</button>
            </form>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {hires.map((h, i) => (
                <div key={h.id} data-testid={`admin-hire-${i}`} className="relative rounded-xl overflow-hidden border border-white/10 group">
                  <img src={hirePhoto(h)} alt={h.name} className="w-full aspect-[3/4] object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-[#0B132B]/90 p-3">
                    <p className="text-xs font-bold text-white">{h.name}</p>
                    <p className="text-[10px] text-orange-400">{h.profession} — {h.country}</p>
                  </div>
                  <button data-testid={`admin-hire-del-${i}`} onClick={() => del(`/admin/hires/${h.id}`)} className="absolute top-2 end-2 bg-red-500/80 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div data-testid="admin-inquiries-panel" className="space-y-4">
            {inquiries.length === 0 && <p className="text-slate-500 text-center py-16">{t("admin.no_inquiries")}</p>}
            {inquiries.map((q, i) => (
              <div key={q.id} data-testid={`admin-inquiry-${i}`} className="rounded-2xl border border-white/10 bg-[#111D38] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-head font-bold text-white text-lg">{q.company_name}</h3>
                    <p className="text-xs text-orange-400 font-bold uppercase tracking-wider mt-0.5">{q.entity_type}</p>
                    <p className="text-sm text-slate-400 mt-2">{q.contact_person} • {q.phone} • {q.email}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                  <select
                    data-testid={`inquiry-status-${i}`}
                    className="field !bg-white/5 !text-white !w-auto text-xs"
                    value={q.status}
                    onChange={(e) => setStatus("inquiries", q.id, e.target.value)}
                  >
                    <option value="new">{t("admin.new")}</option>
                    <option value="contacted">{t("admin.contacted")}</option>
                    <option value="closed">{t("admin.closed")}</option>
                  </select>
                </div>
                <div className="mt-4 space-y-2">
                  {(q.categories || []).map((c, j) => (
                    <div key={j} className="text-xs text-slate-300 rounded-lg bg-[#0B132B] border border-white/5 px-4 py-2.5">
                      <span className="font-bold text-[#F4511E]">{c.label || c.category}</span>
                      <span className="ms-2">
                        {c.workers} × {c.salary ? `${c.salary} SAR` : "—"} • {c.profession_on_visa || "—"} • {c.experience_years}y • Gulf: {c.gulf_experience} • {c.consulate}
                        {c.driver_license ? ` • ${c.driver_license}` : ""}{c.driver_for ? ` • ${c.driver_for}` : ""} • {c.duty_hours}h • shift {c.shift}
                      </span>
                    </div>
                  ))}
                </div>
                {q.notes && <p className="mt-3 text-xs text-slate-500 italic">{q.notes}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "applications" && (
          <div data-testid="admin-applications-panel" className="space-y-4">
            {applications.length === 0 && <p className="text-slate-500 text-center py-16">{t("admin.no_applications")}</p>}
            {applications.map((a, i) => (
              <div key={a.id} data-testid={`admin-application-${i}`} className="rounded-2xl border border-white/10 bg-[#111D38] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-head font-bold text-white text-lg">{a.name}</h3>
                    <p className="text-xs text-orange-400 font-bold uppercase tracking-wider mt-0.5">{a.category}</p>
                    <p className="text-sm text-slate-400 mt-2">{a.phone} • {a.email} • {a.city}</p>
                    <p className="text-xs text-slate-500 mt-1.5">{a.experience_years}y exp • Gulf: {a.gulf_experience} • {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  <select
                    data-testid={`application-status-${i}`}
                    className="field !bg-white/5 !text-white !w-auto text-xs"
                    value={a.status}
                    onChange={(e) => setStatus("applications", a.id, e.target.value)}
                  >
                    <option value="new">{t("admin.new")}</option>
                    <option value="contacted">{t("admin.contacted")}</option>
                    <option value="closed">{t("admin.closed")}</option>
                  </select>
                </div>
                {a.notes && <p className="mt-3 text-xs text-slate-500 italic">{a.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
